import React, { useState, useEffect, useCallback } from 'react';
import { 
  Smartphone, 
  Users, 
  Building2, 
  Image, 
  Palette,
  Star,
  Plus,
  Trash2,
  GripVertical,
  Save,
  RefreshCw,
  Eye,
  Upload,
  ChevronDown,
  ChevronUp,
  Search,
  Check,
  X,
  AlertCircle,
  BookOpen,
  Power
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import '../../styles/AppContentManager.css';

// Spotlight type configuration
const SPOTLIGHT_TYPES = {
  artwork: {
    label: 'Featured Artwork',
    description: 'Individual artworks shown in the carousel at the top of the Discover Artwork page',
    icon: Image,
    color: '#EC4899',
    lookupTable: 'vcs',
    lookupField: 'vc_id',
    displayField: 'title',
    secondaryField: 'vc_id',
    imageField: 'cover_image',
    // Use spotlight_id column (not spotlight_uuid)
    usesSpotlightId: true
  },
  artist: {
    label: 'Featured Artists',
    description: 'Artists shown in the "Featured Artists" card with pill navigation',
    icon: Palette,
    color: '#F59E0B',
    lookupTable: 'users',
    lookupField: 'uuid',
    displayField: 'first_name',
    secondaryField: 'handle',
    imageField: 'profile_img',
    // Artists are identified by preferences.home = 'artist'
    customSearch: true
  },
  gallery: {
    label: 'Featured Galleries',
    description: 'Galleries shown in the "Featured Galleries" card with pill navigation',
    icon: Building2,
    color: '#3B82F6',
    lookupTable: 'galleries',
    lookupField: 'gallery_id',
    displayField: 'name',
    secondaryField: 'address',
    imageField: 'cover_image'
  },
  collection: {
    label: 'Spotlight Collections',
    description: 'Collections shown as promotional cards on the welcome screen',
    icon: BookOpen,
    color: '#8B5CF6',
    lookupTable: 'collections',
    lookupField: 'collection_id',
    displayField: 'title',
    secondaryField: 'description',
    imageField: 'cover_image'
  },
  latest_drop: {
    label: 'Latest Art Drops',
    description: 'Individual artworks shown in the horizontal scroll "Latest Art Drops" section',
    icon: Star,
    color: '#10B981',
    lookupTable: 'vcs',
    lookupField: 'uuid',
    displayField: 'title',
    secondaryField: 'vc_id',
    imageField: 'cover_image'
  },
  art_on_wall: {
    label: 'Find Artwork Background',
    description: 'Background image for the "Find Artwork You Love" card (only 1 active)',
    icon: Image,
    color: '#EC4899',
    lookupTable: 'media_elements',
    lookupField: 'element_id',
    displayField: 'element_id',
    secondaryField: 'source',
    imageField: 'source',
    singleItem: true,
    // Load all On-Wall images automatically
    autoLoad: true,
    filterField: 'title',
    filterValue: 'On-Wall',
    imageTransform: 't_square-300/'
  },
  gallery_events_bg: {
    label: 'Galleries & Events Background',
    description: 'Background image for the "Galleries & Events" card on the welcome screen (only 1 active)',
    icon: Building2,
    color: '#3B82F6',
    lookupTable: 'media_elements',
    lookupField: 'element_id',
    displayField: 'element_id',
    secondaryField: 'source',
    imageField: 'source',
    singleItem: true,
    autoLoad: true,
    filterField: 'title',
    filterValue: 'On-Wall',
    imageTransform: 't_square-300/'
  },
  art_speaks_bg: {
    label: 'Art Speaks Background',
    description: 'Background image for the "Art Speaks" card on the welcome screen (only 1 active)',
    icon: Star,
    color: '#FF6B35',
    lookupTable: 'media_elements',
    lookupField: 'element_id',
    displayField: 'element_id',
    secondaryField: 'source',
    imageField: 'source',
    singleItem: true,
    autoLoad: true,
    filterField: 'title',
    filterValue: 'On-Wall',
    imageTransform: 't_square-300/'
  },
  pulse_bg: {
    label: 'Pulse of Community Background',
    description: 'Background image for the "Pulse of the Community" card on the welcome screen (only 1 active)',
    icon: Users,
    color: '#F97316',
    lookupTable: 'media_elements',
    lookupField: 'element_id',
    displayField: 'element_id',
    secondaryField: 'source',
    imageField: 'source',
    singleItem: true,
    autoLoad: true,
    filterField: 'title',
    filterValue: 'On-Wall',
    imageTransform: 't_square-300/',
    hasLiveToggle: true,
    hasAdminPreviewToggle: true
  }
};

// Helper to add cloudinary transform
const addCloudinaryTransform = (url, transform = 't_fitmax-300/') => {
  if (!url || !url.includes('cloudinary.com')) return url;
  return url.replace('/upload/', `/upload/${transform}`);
};

// Helper to get best display name for artists
const getArtistDisplayName = (user) => {
  if (!user) return 'Unknown';
  // Prefer nickname, then full name, then email
  if (user.nickname) return user.nickname;
  if (user.first_name || user.last_name) {
    return [user.first_name, user.last_name].filter(Boolean).join(' ');
  }
  return user.email || 'Unknown';
};

// Helper to get secondary info for artists (handle + email if different from display)
const getArtistSecondaryInfo = (user) => {
  if (!user) return null;
  const parts = [];
  if (user.handle) parts.push(`@${user.handle}`);
  // Show name if nickname was used as primary
  if (user.nickname && (user.first_name || user.last_name)) {
    const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
    if (fullName) parts.push(fullName);
  }
  return parts.length > 0 ? parts.join(' • ') : user.email;
};

// Helper to get display name for any entity type
const getEntityDisplayName = (type, entity, config) => {
  if (type === 'artist') {
    return getArtistDisplayName(entity);
  }
  return entity[config.displayField] || 'Unknown';
};

// Helper to get secondary info for any entity type
const getEntitySecondaryInfo = (type, entity, config) => {
  if (type === 'artist') {
    return getArtistSecondaryInfo(entity);
  }
  return config.secondaryField ? entity[config.secondaryField] : null;
};

const AppContentManager = () => {
  const [spotlights, setSpotlights] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    artwork: true,
    artist: true,
    gallery: true,
    collection: true,
    latest_drop: true,
    art_on_wall: true,
    gallery_events_bg: true,
    art_speaks_bg: true
  });
  const [searchModals, setSearchModals] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [enrichedData, setEnrichedData] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [coverImageModal, setCoverImageModal] = useState(null); // { spotlightId, collectionId, label }
  const [coverImageOptions, setCoverImageOptions] = useState([]);
  const [coverImageLoading, setCoverImageLoading] = useState(false);

  // Load all spotlights on mount
  useEffect(() => {
    loadSpotlights();
  }, []);

  // Upload image from computer, create media_elements record, and set as spotlight
  const handleImageUpload = async (type, file) => {
    if (!file) return;

    const config = SPOTLIGHT_TYPES[type];
    if (!config) return;

    setUploading(true);
    setUploadPreview(URL.createObjectURL(file));

    try {
      // 1. Upload to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', 'e8mdddgj');
      formData.append('folder', 'app_backgrounds');

      const cloudinaryResponse = await fetch(
        'https://api.cloudinary.com/v1_1/dh9jfogh1/image/upload',
        { method: 'POST', body: formData }
      );

      if (!cloudinaryResponse.ok) {
        throw new Error('Failed to upload image to Cloudinary');
      }

      const cloudinaryData = await cloudinaryResponse.json();

      // 2. Create a media_elements record
      const { data: mediaRecord, error: mediaError } = await supabase
        .from('media_elements')
        .insert({
          source: cloudinaryData.secure_url,
          title: 'On-Wall',
          type: 'image',
          status: 'Public',
          usage_type: 'banner'
        })
        .select()
        .single();

      if (mediaError) throw mediaError;

      // 3. If singleItem, remove existing spotlight first
      if (config.singleItem) {
        const existingItems = spotlights[type] || [];
        for (const existing of existingItems) {
          await supabase
            .from('admin_curated_spotlights')
            .delete()
            .eq('id', existing.id);
        }
      }

      // 4. Create the spotlight record
      const { data: spotlightRecord, error: spotlightError } = await supabase
        .from('admin_curated_spotlights')
        .insert({
          spotlight_type: type,
          spotlight_id: mediaRecord.element_id,
          priority: 1,
          label: config.label
        })
        .select()
        .single();

      if (spotlightError) throw spotlightError;

      // 5. Update local state
      setSpotlights(prev => ({
        ...prev,
        [type]: config.singleItem ? [spotlightRecord] : [...(prev[type] || []), spotlightRecord]
      }));

      setEnrichedData(prev => ({
        ...prev,
        [type]: {
          ...(prev[type] || {}),
          [mediaRecord.element_id]: {
            name: `Element #${mediaRecord.element_id}`,
            secondary: cloudinaryData.secure_url,
            image: cloudinaryData.secure_url
          }
        }
      }));

      setSuccess(`Uploaded and set as ${config.label}`);
      setTimeout(() => setSuccess(null), 3000);
      closeSearchModal(type);

    } catch (e) {
      console.error('Error uploading image:', e);
      setError(`Upload failed: ${e.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setUploading(false);
      setUploadPreview(null);
    }
  };

  const loadSpotlights = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('admin_curated_spotlights')
        .select('*')
        .order('spotlight_type')
        .order('priority', { ascending: true });

      if (fetchError) throw fetchError;

      // Group by spotlight_type
      const grouped = {};
      for (const type of Object.keys(SPOTLIGHT_TYPES)) {
        grouped[type] = (data || []).filter(s => s.spotlight_type === type);
      }

      setSpotlights(grouped);

      // Enrich data with actual names/images
      await enrichSpotlightData(grouped);

    } catch (err) {
      console.error('Error loading spotlights:', err);
      setError('Failed to load spotlight data');
    } finally {
      setLoading(false);
    }
  };

  // Enrich spotlight data with actual entity info (names, images)
  const enrichSpotlightData = async (groupedSpotlights) => {
    const enriched = {};

    for (const [type, items] of Object.entries(groupedSpotlights)) {
      const config = SPOTLIGHT_TYPES[type];
      if (!config || items.length === 0) continue;

      enriched[type] = {};

      // Determine which field to use for lookup
      const usesUuid = config.lookupField === 'uuid';
      const usesId = ['gallery_id', 'collection_id', 'element_id', 'vc_id'].includes(config.lookupField);

      let ids = [];
      if (usesUuid) {
        ids = items.map(i => i.spotlight_uuid).filter(Boolean);
      } else if (usesId) {
        ids = items.map(i => i.spotlight_id).filter(Boolean);
      }

      if (ids.length === 0) continue;

      try {
        let query = supabase.from(config.lookupTable).select('*');
        
        if (usesUuid) {
          query = query.in(config.lookupField, ids);
        } else {
          query = query.in(config.lookupField, ids);
        }

        const { data: entityData, error: entityError } = await query;

        if (!entityError && entityData) {
          for (const entity of entityData) {
            const key = usesUuid ? entity[config.lookupField] : entity[config.lookupField];
            enriched[type][key] = {
              name: type === 'artist' ? getArtistDisplayName(entity) : (entity[config.displayField] || 'Unknown'),
              secondary: type === 'artist' ? getArtistSecondaryInfo(entity) : (config.secondaryField ? entity[config.secondaryField] : null),
              image: entity[config.imageField] || null
            };
          }
        }
      } catch (e) {
        console.error(`Error enriching ${type} data:`, e);
      }
    }

    setEnrichedData(enriched);
  };

  // Toggle section expansion
  const toggleSection = (type) => {
    setExpandedSections(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // Open search modal for adding items
  const openSearchModal = (type) => {
    setSearchModals(prev => ({ ...prev, [type]: true }));
    setSearchQuery('');
    setSearchResults([]);
    
    // Auto-load items for types that don't require search
    const config = SPOTLIGHT_TYPES[type];
    if (config?.autoLoad) {
      loadAutoItems(type);
    }
  };

  // Close search modal
  const closeSearchModal = (type) => {
    setSearchModals(prev => ({ ...prev, [type]: false }));
    setSearchQuery('');
    setSearchResults([]);
  };

  // Load all items for autoLoad types (like art_on_wall)
  const loadAutoItems = async (type) => {
    const config = SPOTLIGHT_TYPES[type];
    if (!config || !config.autoLoad) return;

    setSearchLoading(true);
    try {
      const { data, error } = await supabase
        .from(config.lookupTable)
        .select('*')
        .eq(config.filterField, config.filterValue)
        .order(config.lookupField, { ascending: true });

      if (error) throw error;

      // Filter out already added items
      const existingIds = spotlights[type]?.map(s => s.spotlight_id) || [];
      const filtered = (data || []).filter(item => 
        !existingIds.includes(item[config.lookupField])
      );

      setSearchResults(filtered);
    } catch (e) {
      console.error('Error loading items:', e);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Search for entities to add
  const handleSearch = async (type, query) => {
    setSearchQuery(query);
    
    const config = SPOTLIGHT_TYPES[type];
    if (!config) return;

    // For autoLoad types, don't require search query
    if (config.autoLoad) {
      return; // Items already loaded
    }
    
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);

    try {
      let query_builder;
      
      // Custom search for artists - search by name and filter by preferences
      if (type === 'artist') {
        query_builder = supabase
          .from('users')
          .select('*')
          .eq('preferences->>home', 'artist')
          .or(`first_name.ilike.%${query}%,last_name.ilike.%${query}%,handle.ilike.%${query}%,nickname.ilike.%${query}%`)
          .limit(20);
      } else if (type === 'artwork') {
        // Search artworks by title or vc_id, only show public artworks
        query_builder = supabase
          .from('vcs')
          .select('*')
          .eq('access_control', 'Public')
          .or(`title.ilike.%${query}%,vc_id.eq.${isNaN(query) ? 0 : query}`)
          .order('created_at', { ascending: false })
          .limit(20);
      } else {
        let searchField = config.displayField;
        query_builder = supabase
          .from(config.lookupTable)
          .select('*')
          .ilike(searchField, `%${query}%`)
          .limit(20);
      }

      const { data, error: searchError } = await query_builder;

      if (searchError) throw searchError;

      // Filter out already added items
      const existingIds = spotlights[type]?.map(s => 
        config.lookupField === 'uuid' ? s.spotlight_uuid : s.spotlight_id
      ) || [];

      const filtered = (data || []).filter(item => 
        !existingIds.includes(item[config.lookupField])
      );

      setSearchResults(filtered);
    } catch (e) {
      console.error('Search error:', e);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Add a new spotlight item
  const addSpotlightItem = async (type, entity) => {
    const config = SPOTLIGHT_TYPES[type];
    if (!config) return;

    // Calculate next priority
    const currentItems = spotlights[type] || [];
    const maxPriority = currentItems.length > 0 
      ? Math.max(...currentItems.map(i => i.priority || 0)) 
      : 0;

    const newItem = {
      spotlight_type: type,
      spotlight_uuid: config.lookupField === 'uuid' ? entity[config.lookupField] : null,
      spotlight_id: config.lookupField !== 'uuid' ? entity[config.lookupField] : null,
      priority: maxPriority + 1,
      label: getEntityDisplayName(type, entity, config),
      // Items added to a spotlight list should be visible by default; the
      // LIVE/OFF toggle is for hiding without removing.
      is_live: true,
    };

    try {
      setSaving(true);

      const { data, error: insertError } = await supabase
        .from('admin_curated_spotlights')
        .insert(newItem)
        .select()
        .single();

      if (insertError) throw insertError;

      // Update local state
      setSpotlights(prev => ({
        ...prev,
        [type]: [...(prev[type] || []), data]
      }));

      // Add to enriched data
      setEnrichedData(prev => ({
        ...prev,
        [type]: {
          ...(prev[type] || {}),
          [entity[config.lookupField]]: {
            name: getEntityDisplayName(type, entity, config),
            secondary: getEntitySecondaryInfo(type, entity, config),
            image: entity[config.imageField] || null
          }
        }
      }));

      setSuccess(`Added ${getEntityDisplayName(type, entity, config)} to ${config.label}`);
      setTimeout(() => setSuccess(null), 3000);

      closeSearchModal(type);
    } catch (e) {
      console.error('Error adding spotlight:', e);
      setError('Failed to add item');
      setTimeout(() => setError(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  // Remove a spotlight item
  const removeSpotlightItem = async (type, itemId) => {
    try {
      setSaving(true);

      const { error: deleteError } = await supabase
        .from('admin_curated_spotlights')
        .delete()
        .eq('id', itemId);

      if (deleteError) throw deleteError;

      // Update local state
      setSpotlights(prev => ({
        ...prev,
        [type]: (prev[type] || []).filter(item => item.id !== itemId)
      }));

      setSuccess('Item removed');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      console.error('Error removing spotlight:', e);
      setError('Failed to remove item');
      setTimeout(() => setError(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  // Update priority (reorder)
  const updatePriority = async (type, itemId, direction) => {
    const items = [...(spotlights[type] || [])];
    const currentIndex = items.findIndex(i => i.id === itemId);
    
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= items.length) return;

    // Swap items
    [items[currentIndex], items[newIndex]] = [items[newIndex], items[currentIndex]];

    // Update priorities
    const updates = items.map((item, index) => ({
      id: item.id,
      priority: index + 1
    }));

    try {
      setSaving(true);

      for (const update of updates) {
        await supabase
          .from('admin_curated_spotlights')
          .update({ priority: update.priority })
          .eq('id', update.id);
      }

      // Update local state with new order
      setSpotlights(prev => ({
        ...prev,
        [type]: items.map((item, index) => ({ ...item, priority: index + 1 }))
      }));

    } catch (e) {
      console.error('Error updating priority:', e);
      setError('Failed to update order');
      setTimeout(() => setError(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, type, index) => {
    setDraggedItem({ type, index });
    e.dataTransfer.effectAllowed = 'move';
    // Add a slight delay to allow the drag image to be captured
    setTimeout(() => {
      e.target.closest('.spotlight-item')?.classList.add('dragging');
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.closest('.spotlight-item')?.classList.remove('dragging');
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragOver = (e, type, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Only allow dropping within the same type
    if (draggedItem && draggedItem.type === type && draggedItem.index !== index) {
      setDragOverItem({ type, index });
    }
  };

  const handleDragLeave = (e) => {
    // Only clear if we're leaving the item entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverItem(null);
    }
  };

  const handleDrop = async (e, type, dropIndex) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.type !== type) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    const dragIndex = draggedItem.index;
    
    if (dragIndex === dropIndex) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Reorder the items
    const items = [...(spotlights[type] || [])];
    const [removed] = items.splice(dragIndex, 1);
    items.splice(dropIndex, 0, removed);

    // Update priorities in database
    const updates = items.map((item, index) => ({
      id: item.id,
      priority: index + 1
    }));

    try {
      setSaving(true);

      for (const update of updates) {
        await supabase
          .from('admin_curated_spotlights')
          .update({ priority: update.priority })
          .eq('id', update.id);
      }

      // Update local state with new order
      setSpotlights(prev => ({
        ...prev,
        [type]: items.map((item, index) => ({ ...item, priority: index + 1 }))
      }));

      setSuccess('Order updated');
      setTimeout(() => setSuccess(null), 2000);

    } catch (err) {
      console.error('Error updating order:', err);
      setError('Failed to update order');
      setTimeout(() => setError(null), 3000);
    } finally {
      setSaving(false);
      setDraggedItem(null);
      setDragOverItem(null);
    }
  };

  // Get enriched display info for an item
  const getItemDisplay = (type, item) => {
    const config = SPOTLIGHT_TYPES[type];
    const lookupKey = config.lookupField === 'uuid' ? item.spotlight_uuid : item.spotlight_id;
    const enrichedItem = enrichedData[type]?.[lookupKey];

    return {
      name: enrichedItem?.name || item.label || `ID: ${lookupKey}`,
      secondary: enrichedItem?.secondary || null,
      image: enrichedItem?.image ? addCloudinaryTransform(enrichedItem.image, 'c_thumb,w_100,h_100/') : null
    };
  };

  // Open cover image picker for a collection spotlight
  const openCoverImagePicker = async (spotlightItem) => {
    const collectionId = spotlightItem.spotlight_id;
    setCoverImageModal({ spotlightId: spotlightItem.id, collectionId, label: spotlightItem.label });
    setCoverImageLoading(true);
    setCoverImageOptions([]);

    try {
      // Fetch artworks in this collection via collection_vc_mapping -> vcs
      const { data: mappings, error: mapError } = await supabase
        .from('collection_vc_mapping')
        .select('vc_id')
        .eq('collection_id', collectionId)
        .eq('accepted', true)
        .order('order_num', { ascending: true });

      if (mapError) throw mapError;

      const vcIds = (mappings || []).map(m => m.vc_id).filter(Boolean);
      if (vcIds.length === 0) {
        setCoverImageOptions([]);
        setCoverImageLoading(false);
        return;
      }

      const { data: artworks, error: artError } = await supabase
        .from('vcs')
        .select('vc_id, title, cover_image')
        .in('vc_id', vcIds);

      if (artError) throw artError;

      // Preserve collection order
      const artMap = {};
      for (const a of (artworks || [])) {
        artMap[a.vc_id] = a;
      }
      const ordered = vcIds.map(id => artMap[id]).filter(Boolean).filter(a => a.cover_image);
      setCoverImageOptions(ordered);
    } catch (e) {
      console.error('Error loading collection artworks:', e);
      setCoverImageOptions([]);
    } finally {
      setCoverImageLoading(false);
    }
  };

  // Toggle admin_preview flag on a spotlight record (admins only in the app)
  const toggleAdminPreview = async (type, itemId, currentValue) => {
    try {
      setSaving(true);
      const { error: updateError } = await supabase
        .from('admin_curated_spotlights')
        .update({ admin_preview: !currentValue })
        .eq('id', itemId);

      if (updateError) throw updateError;

      setSpotlights(prev => ({
        ...prev,
        [type]: (prev[type] || []).map(item =>
          item.id === itemId ? { ...item, admin_preview: !currentValue } : item
        )
      }));

      setSuccess(`Admin preview is now ${!currentValue ? 'ON' : 'OFF'} — admins will ${!currentValue ? 'see' : 'not see'} this card`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      console.error('Error toggling admin_preview:', e);
      setError('Failed to update admin preview status');
      setTimeout(() => setError(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  // Toggle is_live flag on a spotlight record
  const toggleIsLive = async (type, itemId, currentValue) => {
    try {
      setSaving(true);
      const { error: updateError } = await supabase
        .from('admin_curated_spotlights')
        .update({ is_live: !currentValue })
        .eq('id', itemId);

      if (updateError) throw updateError;

      setSpotlights(prev => ({
        ...prev,
        [type]: (prev[type] || []).map(item =>
          item.id === itemId ? { ...item, is_live: !currentValue } : item
        )
      }));

      setSuccess(`Pulse card is now ${!currentValue ? 'LIVE' : 'hidden'} in the app`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      console.error('Error toggling is_live:', e);
      setError('Failed to update live status');
      setTimeout(() => setError(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  // Set cover image on a spotlight record
  const setCoverImage = async (spotlightId, coverImageUrl) => {
    try {
      setSaving(true);
      const { error: updateError } = await supabase
        .from('admin_curated_spotlights')
        .update({ cover_image: coverImageUrl })
        .eq('id', spotlightId);

      if (updateError) throw updateError;

      // Update local state
      setSpotlights(prev => {
        const updated = { ...prev };
        for (const [type, items] of Object.entries(updated)) {
          updated[type] = items.map(item =>
            item.id === spotlightId ? { ...item, cover_image: coverImageUrl } : item
          );
        }
        return updated;
      });

      setSuccess('Cover image updated');
      setTimeout(() => setSuccess(null), 3000);
      setCoverImageModal(null);
    } catch (e) {
      console.error('Error setting cover image:', e);
      setError('Failed to set cover image');
      setTimeout(() => setError(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  // Render a spotlight section
  const renderSpotlightSection = (type) => {
    const config = SPOTLIGHT_TYPES[type];
    const items = spotlights[type] || [];
    const isExpanded = expandedSections[type];
    const IconComponent = config.icon;

    return (
      <div key={type} className="spotlight-section">
        <div 
          className="spotlight-section-header"
          onClick={() => toggleSection(type)}
          style={{ borderLeftColor: config.color }}
        >
          <div className="spotlight-section-title">
            <IconComponent size={20} style={{ color: config.color }} />
            <div>
              <h3>{config.label}</h3>
              <p className="spotlight-section-description">{config.description}</p>
            </div>
          </div>
          <div className="spotlight-section-meta">
            {config.hasAdminPreviewToggle && items.length > 0 && (
              <button
                className={`admin-preview-toggle-btn ${items[0]?.admin_preview ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleAdminPreview(type, items[0].id, items[0].admin_preview);
                }}
                disabled={saving}
                title={items[0]?.admin_preview ? 'Admin preview ON — admins see this card even when LIVE is OFF' : 'Admin preview OFF — click to let admins preview this card'}
              >
                <Eye size={14} />
                <span>ADMIN {items[0]?.admin_preview ? 'ON' : 'OFF'}</span>
              </button>
            )}
            {config.hasLiveToggle && items.length > 0 && (
              <button
                className={`live-toggle-btn ${items[0]?.is_live ? 'live' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleIsLive(type, items[0].id, items[0].is_live);
                }}
                disabled={saving}
                title={items[0]?.is_live ? 'Card is LIVE — click to hide' : 'Card is hidden — click to go live'}
              >
                <Power size={14} />
                <span>{items[0]?.is_live ? 'LIVE' : 'OFF'}</span>
              </button>
            )}
            <span className="spotlight-count">{items.length} item{items.length !== 1 ? 's' : ''}</span>
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        </div>

        {isExpanded && (
          <div className="spotlight-section-content">
            {items.length === 0 ? (
              <div className="spotlight-empty">
                <p>No items configured. Add some to show on the app.</p>
              </div>
            ) : (
              <div className="spotlight-items-list">
                {items.map((item, index) => {
                  const display = getItemDisplay(type, item);
                  const isDragging = draggedItem?.type === type && draggedItem?.index === index;
                  const isDragOver = dragOverItem?.type === type && dragOverItem?.index === index;
                  return (
                    <div 
                      key={item.id} 
                      className={`spotlight-item ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, type, index)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => handleDragOver(e, type, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, type, index)}
                    >
                      <div className="spotlight-item-drag" title="Drag to reorder">
                        <GripVertical size={16} />
                      </div>
                      
                      {display.image && (
                        <div className="spotlight-item-image">
                          <img src={display.image} alt={display.name} />
                        </div>
                      )}
                      
                      <div className="spotlight-item-info">
                        <span className="spotlight-item-name">{display.name}</span>
                        {display.secondary && (
                          <span className="spotlight-item-secondary">{display.secondary}</span>
                        )}
                      </div>

                      <div className="spotlight-item-priority">
                        <span className="priority-badge">#{item.priority || index + 1}</span>
                      </div>

                      {/* Cover image thumbnail for collections */}
                      {type === 'collection' && (
                        <div className="spotlight-item-cover">
                          {item.cover_image ? (
                            <img
                              src={addCloudinaryTransform(item.cover_image, 'c_thumb,w_80,h_60,g_auto/')}
                              alt="Cover"
                              className="spotlight-cover-thumb"
                              onClick={() => openCoverImagePicker(item)}
                              title="Click to change cover image"
                            />
                          ) : (
                            <button
                              className="set-cover-btn"
                              onClick={() => openCoverImagePicker(item)}
                              disabled={saving}
                              title="Set cover image"
                            >
                              <Image size={14} />
                              <span>Set Cover</span>
                            </button>
                          )}
                        </div>
                      )}

                      <div className="spotlight-item-actions">
                        <button 
                          className="priority-btn"
                          onClick={() => updatePriority(type, item.id, 'up')}
                          disabled={index === 0 || saving}
                          title="Move up"
                        >
                          <ChevronUp size={16} />
                        </button>
                        <button 
                          className="priority-btn"
                          onClick={() => updatePriority(type, item.id, 'down')}
                          disabled={index === items.length - 1 || saving}
                          title="Move down"
                        >
                          <ChevronDown size={16} />
                        </button>
                        <button 
                          className="remove-btn"
                          onClick={() => removeSpotlightItem(type, item.id)}
                          disabled={saving}
                          title="Remove"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add button - show for non-single items, or add options for single items with no current entry */}
            {!config.singleItem && (
              <button 
                className="add-spotlight-btn"
                onClick={() => openSearchModal(type)}
                disabled={saving}
              >
                <Plus size={16} />
                Add {config.label.replace('Featured ', '').replace('Spotlight ', '')}
              </button>
            )}
            {config.singleItem && items.length === 0 && (
              <div className="spotlight-bg-actions">
                <button 
                  className="add-spotlight-btn"
                  onClick={() => openSearchModal(type)}
                  disabled={saving}
                >
                  <Plus size={16} />
                  Choose from Media Library
                </button>
                <label className="add-spotlight-btn upload-btn" style={{ cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.5 : 1 }}>
                  <Upload size={16} />
                  Upload from Computer
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    disabled={saving}
                    onChange={(e) => {
                      if (e.target.files[0]) handleImageUpload(type, e.target.files[0]);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            )}
            {config.singleItem && items.length > 0 && (
              <div className="spotlight-bg-actions">
                <button 
                  className="add-spotlight-btn change-btn"
                  onClick={() => openSearchModal(type)}
                  disabled={saving}
                >
                  <RefreshCw size={16} />
                  Choose from Media Library
                </button>
                <label className="add-spotlight-btn upload-btn" style={{ cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.5 : 1 }}>
                  <Upload size={16} />
                  Upload from Computer
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    disabled={saving}
                    onChange={(e) => {
                      if (e.target.files[0]) handleImageUpload(type, e.target.files[0]);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            )}
          </div>
        )}

        {/* Search Modal */}
        {searchModals[type] && (
          <div className="search-modal-overlay" onClick={() => closeSearchModal(type)}>
            <div className={`search-modal ${config.autoLoad ? 'search-modal-wide' : ''}`} onClick={e => e.stopPropagation()}>
              <div className="search-modal-header">
                <h3>{config.autoLoad ? `Select ${config.label}` : `Add to ${config.label}`}</h3>
                <button className="close-btn" onClick={() => closeSearchModal(type)}>
                  <X size={20} />
                </button>
              </div>

              {/* Upload from computer option for autoLoad (image) types */}
              {config.autoLoad && (
                <div className="upload-section">
                  {uploading ? (
                    <div className="upload-progress">
                      {uploadPreview && (
                        <img src={uploadPreview} alt="Upload preview" className="upload-preview-img" />
                      )}
                      <div className="upload-progress-text">
                        <RefreshCw size={16} className="spinning" />
                        <span>Uploading...</span>
                      </div>
                    </div>
                  ) : (
                    <label className="upload-dropzone">
                      <Upload size={24} />
                      <span className="upload-dropzone-text">Upload from computer</span>
                      <span className="upload-dropzone-hint">Click to select an image file</span>
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          if (e.target.files[0]) handleImageUpload(type, e.target.files[0]);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  )}
                  <div className="upload-divider">
                    <span>or choose from media library</span>
                  </div>
                </div>
              )}
              
              {/* Only show search input for non-autoLoad types */}
              {!config.autoLoad && (
                <div className="search-modal-search">
                  <Search size={16} />
                  <input
                    type="text"
                    placeholder={`Search ${config.label.toLowerCase()}...`}
                    value={searchQuery}
                    onChange={(e) => handleSearch(type, e.target.value)}
                    autoFocus
                  />
                </div>
              )}

              <div className={`search-modal-results ${config.autoLoad ? 'image-grid-results' : ''}`}>
                {searchLoading ? (
                  <div className="search-loading">
                    <RefreshCw size={20} className="spinning" />
                    <span>{config.autoLoad ? 'Loading images...' : 'Searching...'}</span>
                  </div>
                ) : searchResults.length > 0 ? (
                  searchResults.map(result => (
                    <div 
                      key={result[config.lookupField]} 
                      className={`search-result-item ${config.autoLoad ? 'image-grid-item' : ''}`}
                      onClick={() => addSpotlightItem(type, result)}
                    >
                      {result[config.imageField] && (
                        <div className={`search-result-image ${config.autoLoad ? 'large-image' : ''}`}>
                          <img 
                            src={addCloudinaryTransform(result[config.imageField], config.imageTransform || 'c_thumb,w_60,h_60/')} 
                            alt={`Image ${result[config.lookupField]}`} 
                          />
                        </div>
                      )}
                      {!config.autoLoad && (
                        <div className="search-result-info">
                          <span className="search-result-name">{getEntityDisplayName(type, result, config)}</span>
                          {getEntitySecondaryInfo(type, result, config) && (
                            <span className="search-result-secondary">
                              {String(getEntitySecondaryInfo(type, result, config)).substring(0, 60)}
                            </span>
                          )}
                        </div>
                      )}
                      {config.autoLoad && (
                        <div className="image-grid-label">#{result[config.lookupField]}</div>
                      )}
                      <Plus size={16} className="add-icon" />
                    </div>
                  ))
                ) : config.autoLoad ? (
                  <div className="search-empty">
                    <p>No images found with title "On-Wall"</p>
                  </div>
                ) : searchQuery.length >= 2 ? (
                  <div className="search-empty">
                    <p>No results found for "{searchQuery}"</p>
                  </div>
                ) : (
                  <div className="search-hint">
                    <p>Type at least 2 characters to search</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="app-content-manager">
        <div className="loading-state">
          <RefreshCw size={32} className="spinning" />
          <p>Loading app content settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-content-manager">
      {/* Header */}
      <div className="acm-header">
        <div className="acm-header-content">
          <div className="acm-header-icon">
            <Smartphone size={28} />
          </div>
          <div className="acm-header-text">
            <h1>App Content Manager</h1>
            <p>Control what appears on the VibrantCast app's welcome screen and Discover Artwork page. Changes appear immediately.</p>
          </div>
        </div>
        <div className="acm-header-actions">
          <button 
            className="refresh-btn"
            onClick={loadSpotlights}
            disabled={loading || saving}
          >
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Notifications */}
      {error && (
        <div className="acm-notification error">
          <AlertCircle size={16} />
          <span>{error}</span>
          <button onClick={() => setError(null)}><X size={14} /></button>
        </div>
      )}
      
      {success && (
        <div className="acm-notification success">
          <Check size={16} />
          <span>{success}</span>
          <button onClick={() => setSuccess(null)}><X size={14} /></button>
        </div>
      )}

      {/* Preview Info */}
      <div className="acm-preview-info">
        <Eye size={16} />
        <span>Changes are saved automatically and will appear in the app on the next screen load.</span>
      </div>

      {/* Spotlight Sections */}
      <div className="spotlight-sections">
        {Object.keys(SPOTLIGHT_TYPES).map(type => renderSpotlightSection(type))}
      </div>

      {/* Cover Image Picker Modal for Collections */}
      {coverImageModal && (
        <div className="search-modal-overlay" onClick={() => setCoverImageModal(null)}>
          <div className="search-modal search-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="search-modal-header">
              <h3>Choose Cover Image — {coverImageModal.label}</h3>
              <button className="close-btn" onClick={() => setCoverImageModal(null)}>
                <X size={20} />
              </button>
            </div>
            <p style={{ padding: '0 16px 8px', color: '#6b7280', fontSize: '13px' }}>
              Select an artwork from this collection to use as the card background in the app.
            </p>
            <div className="search-modal-results image-grid-results">
              {coverImageLoading ? (
                <div className="search-loading">
                  <RefreshCw size={20} className="spinning" />
                  <span>Loading collection artworks...</span>
                </div>
              ) : coverImageOptions.length > 0 ? (
                coverImageOptions.map(art => (
                  <div
                    key={art.vc_id}
                    className="search-result-item image-grid-item"
                    onClick={() => setCoverImage(coverImageModal.spotlightId, art.cover_image)}
                  >
                    <div className="search-result-image large-image">
                      <img
                        src={addCloudinaryTransform(art.cover_image, 'c_fill,w_200,h_150,g_auto/')}
                        alt={art.title || `VC ${art.vc_id}`}
                      />
                    </div>
                    <div className="image-grid-label">{art.title || `#${art.vc_id}`}</div>
                    <Check size={16} className="add-icon" />
                  </div>
                ))
              ) : (
                <div className="search-empty">
                  <p>No artworks with images found in this collection.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppContentManager;
